<template>
  <WrappedForm
    ref="form"
    class="education-editor"
    labelWidth="150px"
    :model="educationData"
    :formLines="formLines"
  ></WrappedForm>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import moment from 'moment'

const i18nPrefix = 'admin.application.candidate'

export default {
  components: {
    WrappedForm
  },
  props: {
    education: {
      required: false
    },
    levelList: {
      type: Array,
      default: null
    },
    academicRatingList: {
      type: Array,
      default: null
    },
    majorList: {
      type: Array,
      default: null
    },
    schoolList: {
      type: Array,
      default: null
    },
    requiredField: {
      type: Array
    },
    hiddenField: {
      type: Array
    },
    displayField: {
      type: Array
    },
    isCandidate: {
      type: Boolean,
      default: false
    },
    searchSchoolList: {
      type: Function,
      required: true
    },
    searchMajorList: {
      type: Function,
      required: true
    },
    isMiniResume: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix,
      educationData: {
        level: null,
        majorName: null,
        majorId: null,
        startAndEndDate: null,
        schoolName: null,
        schoolId: null,
        academicRating: null,
        gpa: null,
        summary: null
      },
      school: {
        data: null,
        currentData: null,
        page: 1,
        pageSize: 10
      },
      major: {
        data: null,
        currentData: null,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    formLines () {
      const vm = this
      let requiredField = this.requiredField?.length ? [...this.requiredField] : ['degreeLevel', 'majorName', 'startDate', 'endDate', 'schoolName']
      // hr not need any check
      if (!this.isCandidate) {
        requiredField = []
      }

      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }

      const level = vm.educationData.degreeLevel
      if (['Junior School', 'High School'].includes(level)) {
        hiddenField.push(...['majorName', 'academicRating', 'gpa', 'summary'])
      }

      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const lines = {
        degreeLevel: {
          hidden: hiddenField.includes('degreeLevel') && !displayField.includes('degreeLevel'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.educationLevel`),
          option: {
            data: this.levelList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true, popperAppendToBody: false },
          rules: [
            { required: requiredField.includes('degreeLevel'), message: this.$t(`${this.i18nPrefix}.message.educationRequired`), trigger: 'blur' }
          ]
        },
        schoolName: {
          hidden: hiddenField.includes('schoolName') && !displayField.includes('schoolName'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.school`),
          option: {
            valueKey: 'value',
            labelKey: 'value'
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.input`),
          props: {
            allowCreate: true,
            clearable: true,
            noDataText: this.$t(`${this.i18nPrefix}.placeholder.pleaseStartTyping`),
            debounce: 500
          },
          filterMethod: async (keywords) => {
            const filteredList = await this.searchSchoolList(keywords)
            const school = vm.school
            school.page = 1
            school.currentData = filteredList
            school.data = _.slice(filteredList, 0, school.pageSize)
            return school.data
          },
          events: {
            change: this.onSchoolSelect,
            'visible-change': (visible) => {
              if (!visible) {
                vm.school.currentData = []
              }
            }
          },
          loadMore: (filter) => {
            const school = vm.school
            if (school.currentData && school.page * school.pageSize > school.currentData.length) {
              return
            }
            const data = _.slice(school.currentData, school.page * school.pageSize, (school.page + 1) * school.pageSize)
            school.page += 1
            filter.option.data.push(...data)
          },
          rules: [
            { required: requiredField.includes('schoolName'), message: this.$t(`${this.i18nPrefix}.message.schoolRequired`), trigger: 'change' }
          ]
        },
        majorName: {
          hidden: hiddenField.includes('majorName') && !displayField.includes('majorName'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.major`),
          option: {
            valueKey: 'value',
            labelKey: 'value'
          },
          props: {
            allowCreate: true,
            clearable: true,
            noDataText: this.$t(`${this.i18nPrefix}.placeholder.pleaseStartTyping`),
            debounce: 500
          },
          filterMethod: async (keywords) => {
            const filteredList = await this.searchMajorList(keywords)
            const major = vm.major
            major.page = 1
            major.currentData = filteredList
            major.data = _.slice(filteredList, 0, major.pageSize)
            return major.data
          },
          events: {
            change: this.onMajorSelect,
            'visible-change': (visible) => {
              if (!visible) {
                vm.major.currentData = []
              }
            }
          },
          loadMore: (filter) => {
            const major = vm.major
            if (major.currentData && major.page * major.pageSize > major.currentData.length) {
              return
            }
            const data = _.slice(major.currentData, major.page * major.pageSize, (major.page + 1) * major.pageSize)
            major.page += 1
            filter.option.data.push(...data)
          },
          rules: [
            { required: requiredField.includes('majorName'), message: this.$t(`${this.i18nPrefix}.message.majorRequired`), trigger: 'change' }
          ]
        },
        startDate: {
          hidden: hiddenField.includes('startDate') && !displayField.includes('startDate'),
          elType: 'el-date-picker',
          label: this.$t(`${this.i18nPrefix}.label.startDate`),
          props: {
            type: 'month',
            clearable: false,
            align: 'center',
            format: 'yyyy-MM',
            valueFormat: 'yyyy-MM',
            pickerOptions: {
              disabledDate (time) {
                return time.getTime() > Date.now()
              }
            }
          },
          rules: [{
            trigger: 'change',
            validator (rule, value, callback) {
              const endDate = vm.educationData.endDate
              if (value && endDate && moment(value).isAfter(endDate, 'month')) {
                callback(new Error(vm.$t('admin.application.candidate.message.startDateAfterEndDate')))
                return
              }
              callback()
            }
          },
          {
            required: requiredField.includes('startDate'),
            message: this.$t(`${this.i18nPrefix}.message.startDateRequired`),
            trigger: 'blur'
          }]
        },
        endDate: {
          hidden: hiddenField.includes('endDate') && !displayField.includes('endDate'),
          elType: 'el-date-picker',
          label: this.$t(`${this.i18nPrefix}.label.endDate`),
          props: {
            type: 'month',
            clearable: false,
            align: 'center',
            format: 'yyyy-MM',
            valueFormat: 'yyyy-MM'
          },
          rules: [{
            trigger: 'change',
            validator (rule, value, callback) {
              const startDate = vm.educationData.startDate
              if (value && startDate && moment(value).isBefore(startDate, 'month')) {
                callback(new Error(vm.$t('admin.application.candidate.message.startDateAfterEndDate')))
                return
              }
              callback()
            }
          },
          {
            required: requiredField.includes('endDate'),
            message: this.$t(`${this.i18nPrefix}.message.endDateRequired`),
            trigger: 'blur'
          }]
        },
        academicRating: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.academicRating`),
          hidden: hiddenField.includes('academicRating') && !displayField.includes('academicRating'),
          option: {
            data: this.academicRatingList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true, popperAppendToBody: false }
        },
        gpa: {
          hidden: hiddenField.includes('gpa') && !displayField.includes('gpa'),
          label: this.$t(`${this.i18nPrefix}.label.gpa`),
          class: 'major-form-line',
          placeholder: this.$t(`${this.i18nPrefix}.label.gpaInfo`),
          props: {
            type: 'number',
            min: 0,
            max: 4
          },
          rules: [{
            trigger: 'change',
            validator (rule, value, callback) {
              const gpa = _.toNumber(vm.educationData.gpa)
              if (value && (gpa > 4 || gpa < 0)) {
                callback(new Error(vm.$t('admin.application.candidate.message.gpaDataValueError')))
                return
              }
              callback()
            }
          }]
        },
        summary: {
          placeholder: this.$t(`${i18nPrefix}.label.summaryInfo`),
          hidden: hiddenField.includes('summary') && !displayField.includes('summary'),
          label: this.$t(`${this.i18nPrefix}.label.summary`),
          props: {
            type: 'textarea',
            autosize: { minRows: 5 }
          }
        }
      }
      return lines
    }
  },
  created () {

  },
  methods: {
    async getFormRef () {
      if (!this.$refs.form) {
        await this.$nextTick()
      }
      return this.$refs.form
    },
    onSchoolSelect (selected) {
      const school = _.find(this.schoolList, { value: selected })
      if (school) {
        this.educationData.schoolId = school.id
      } else {
        this.educationData.schoolId = null
      }
    },
    onMajorSelect (selected) {
      const major = _.find(this.majorList, { value: selected })
      if (major) {
        this.educationData.majorId = major.id
      } else {
        this.educationData.majorId = null
      }
    },
    toData (education) {
      if (!education) {
        education = {}
      }
      return {
        degreeLevel: education.degreeLevel,
        majorName: education.majorName,
        majorId: education.majorId,
        schoolName: education.schoolName,
        schoolId: education.schoolId,
        startDate: education.startDate,
        endDate: education.endDate,
        summary: education.summary,
        academicRating: education.academicRating,
        gpa: education.gpa
      }
    },
    clearData () {
      this.educationData = this.toData(null)
      this.$nextTick(() => this.clearValidate())
    },
    async clearValidate () {
      const form = await this.getFormRef()
      form.clearValidate()
    },
    async submit () {
      const form = await this.getFormRef()
      const isValid = await form.submit()
      if (!isValid) {
        return null
      } else {
        const education = this.educationData
        return {
          degreeLevel: education.degreeLevel,
          major: education.major,
          majorId: education.majorId,
          majorName: education.majorName,
          school: education.school,
          schoolId: education.schoolId,
          schoolName: education.schoolName,
          startDate: education.startDate,
          endDate: education.endDate,
          summary: education.summary,
          academicRating: education.academicRating,
          gpa: education.gpa
        }
      }
    }
  },
  watch: {
    education: {
      handler () {
        this.educationData = this.toData(this.education)
        this.$nextTick(() => this.clearValidate())
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.education-editor {
  ::v-deep {
    .el-select-dropdown {
      max-width: 48%;
    }

    .el-textarea {
      word-break: initial
    }
  }
}
</style>
